// Debug: Log when file loads

class VariantButtonHandler extends HTMLElement {
  constructor() {
    super();
    this.setupButton();
    this.setupVariantSelectors();
  }

  setupButton() {
    // Find the closest product form
    this.productForm = this.closest('product-form');
    if (!this.productForm) return;

    // Find both desktop and mobile submit buttons
    this.form = this.productForm.querySelector('form');
    this.desktopButton = document.querySelector('#product-form-desk [type="submit"]');
    this.mobileButton = document.querySelector('#product-form-mobile [type="submit"]');
    
    if (!this.desktopButton && !this.mobileButton) return;
    
    // Store the initial button state when available
    const initialSpan = (this.desktopButton || this.mobileButton).querySelector('.row.a_center');
    if (initialSpan) {
      // Store the entire initial HTML structure
      this.availableStateHtml = initialSpan.outerHTML;
    } else {
      // Get the default HTML from the product-form data attribute
      this.availableStateHtml = this.productForm.dataset.defaultButtonHtml;
    }
    
    // Listen for variant changes
    this.variantRadios = this.productForm.querySelector('variant-radios');
    if (this.variantRadios) {
      this.variantRadios.addEventListener('change', this.onVariantChange.bind(this));
    }
    document.addEventListener('variant:change', this.onVariantChange.bind(this));

    // Get initial variant
    const initialVariantId = this.form.querySelector('[name="id"]').value;
    const variantsJson = this.variantRadios?.querySelector('script[type="application/json"]');
    if (variantsJson) {
      const variants = JSON.parse(variantsJson.textContent);
      const initialVariant = variants.find(v => v.id === Number(initialVariantId));
      if (initialVariant) {
        this.updateButtonState(initialVariant);
        this.updateMobilePrice(initialVariant);
      }
    }
  }

  setupVariantSelectors() {
    // Find all select dropdowns
    this.variantSelects = this.productForm?.querySelectorAll('select.variant-select');
    if (!this.variantSelects) return;

    this.variantSelects.forEach(select => {
      select.addEventListener('change', this.onVariantSelectChange.bind(this));
    });
  }

  onVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant) return;
    this.updateButtonState(variant);
    this.updateMobilePrice(variant);
  }

  onVariantSelectChange(event) {
    const select = event.target;
    const optionIndex = select.getAttribute('data-option-index');
    const value = select.value;
    
    // Find and update corresponding radio button
    const radio = this.productForm.querySelector(`input[type="radio"][data-option-value="${value}"][name="${select.name.replace('-mobile', '')}"]`);
    if (radio) {
      radio.checked = true;
      // Create and dispatch a change event on the variant-radios element
      const variantRadios = this.productForm.querySelector('variant-radios');
      if (variantRadios) {
        variantRadios.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Get the current variant based on all selected options
    const currentVariant = this.getCurrentVariant();
    if (currentVariant) {
      this.updateButtonState(currentVariant);
      this.updateMobilePrice(currentVariant);
    }
  }

  updateMobilePrice(variant) {
    if (!variant) return;
    
    const mobilePriceElement = document.querySelector('.prod_price_mob');
    if (!mobilePriceElement) return;

    // Classes from color-swatches.js
    const classes = {
      soldOut: 'price--sold-out',
      onSale: 'price--on-sale',
      noCompare: 'price--no-compare'
    };
    
    // Get the price container
    const priceContainer = mobilePriceElement.querySelector('.price');
    if (!priceContainer) return;
    
    // Get price elements
    const regularPrice = mobilePriceElement.querySelector('.price__regular .price-item--regular');
    const comparePrice = mobilePriceElement.querySelector('.price__sale .price-item--regular');
    const salePrice = mobilePriceElement.querySelector('.price__sale .price-item--sale');
    
    if (!regularPrice) return;
    
    // Remove all price classes first
    priceContainer.classList.remove(classes.soldOut, classes.onSale, classes.noCompare);
    
    // Format prices using the theme's currency formatter
    const formattedPrice = this.formatMoney(variant.price);
    const formattedComparePrice = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : '';
    
    if (!variant.available) {
      // Sold out
      priceContainer.classList.add(classes.soldOut);
    } else if (variant.compare_at_price > variant.price) {
      // On sale
      priceContainer.classList.add(classes.onSale);
      if (comparePrice && salePrice) {
        comparePrice.innerHTML = formattedComparePrice;
        salePrice.innerHTML = formattedPrice;
      }
    } else {
      // Regular price
      regularPrice.innerHTML = formattedPrice;
    }
  }

  formatMoney(cents) {
    if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, window.shopSettings?.moneyFormat);
    }
    
    // Fallback if theme.Currency is not available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    
    // Basic fallback formatter
    return '$' + (cents / 100).toFixed(2);
  }

  getCurrentVariant() {
    const variantsJson = this.variantRadios?.querySelector('script[type="application/json"]');
    if (!variantsJson) return null;
    
    const variants = JSON.parse(variantsJson.textContent);
    const selectedOptions = Array.from(this.variantSelects).map(select => select.value);
    
    return variants.find(variant => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
  }

  updateButtonState(variant) {
    // Update desktop button if it exists
    if (this.desktopButton) {
      this.updateSingleButton(this.desktopButton, variant);
    }

    // Update mobile button if it exists
    if (this.mobileButton) {
      this.updateSingleButton(this.mobileButton, variant);
    }

    // Update select dropdowns
    this.updateSelectOptions(variant);

    // Update hidden variant ID input
    const variantInput = this.form?.querySelector('input[name="id"]');
    if (variantInput && variant) {
      variantInput.value = variant.id;
    }
  }

  updateSingleButton(button, variant) {
    // Update button state
    button.disabled = !variant.available;

    // Update button text and icon
    if (variant.available) {
      if (button.hasAttribute('data-pre-order')) {
        button.innerHTML = window.variantStrings.preOrder;
      } else if (this.availableStateHtml) {
        button.innerHTML = this.availableStateHtml;
      }
    } else {
      button.innerHTML = window.variantStrings.soldOut;
    }
  }

  updateSelectOptions(variant) {
    if (!this.variantSelects) return;
    
    this.variantSelects.forEach(select => {
      const optionIndex = parseInt(select.getAttribute('data-option-index')) - 1;
      const options = select.options;
      
      for (let i = 0; i < options.length; i++) {
        const option = options[i];
        const variantForOption = this.findVariantForOptionAtIndex(option.value, optionIndex);
        
        if (!variantForOption || !variantForOption.available) {
          option.disabled = true;
          if (!option.textContent.includes('- Sold Out')) {
            option.textContent = `${option.value} - Sold Out`;
          }
        } else {
          option.disabled = false;
          option.textContent = option.value.replace(' - Sold Out', '');
        }
      }
    });
  }

  findVariantForOptionAtIndex(value, optionIndex) {
    const variantsJson = this.variantRadios?.querySelector('script[type="application/json"]');
    if (!variantsJson) return null;
    
    const variants = JSON.parse(variantsJson.textContent);
    const currentSelections = Array.from(this.variantSelects).map(select => select.value);
    currentSelections[optionIndex] = value;
    
    return variants.find(variant => 
      variant.options.every((option, index) => option === currentSelections[index])
    );
  }
}

// Define our new component
customElements.define('variant-button-handler', VariantButtonHandler);

// Add a DOM ready check
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded');
  // Add our component to each product form
  document.querySelectorAll('product-form').forEach(form => {
    if (!form.querySelector('variant-button-handler')) {
      const handler = document.createElement('variant-button-handler');
      form.appendChild(handler);
    }
  });
});
